/*
 * OBS Licensed-Pro Registration Form Helper
 * -----------------------------------------
 * License Number + State submit as customer[note][License Number] and
 * customer[note][State] — Shopify natively combines them into the customer's
 * Notes panel in Admin. No JS data-wiring needed.
 *
 * What this script does:
 *   1. Blocks form submission if the "I certify…" checkbox is unchecked
 *   2. Anti-autofill backup for the License Number field. The input is
 *      readonly-until-focus (see the input markup), which stops Chrome from
 *      autofilling at load. As a second layer, if an email-shaped value
 *      still lands in the field, we wipe it — and block submit if one
 *      somehow survives.
 *   3. Blocks a blank or junk License Number, and a blank State.
 *
 * The check runs on `window` in the capture phase, before Shopify's hCaptcha
 * handler on the form. That handler ignores preventDefault(): once its token
 * is back it calls the form's native submit(), which skips every submit
 * listener. While the check sat on the form itself, the error showed and the
 * account was created anyway (20 sign-ups with no license number,
 * 2026-08-24..10-17). Stopping the event here means the captcha never sends
 * a form that failed.
 *
 * Idempotent — safe to load on every page.
 */
(function () {
  'use strict';

  function looksLikeEmail(v) {
    return typeof v === 'string' && v.indexOf('@') > -1;
  }

  /*
   * Junk-license check. Deliberately type-agnostic: cosmetology, barber,
   * esthetician, and nail licenses all vary by state, so we validate SHAPE
   * only — never a per-type format that could reject a real license.
   * Returns an error string, or null when the value is plausible.
   */
  function licenseProblem(raw) {
    var v = String(raw || '').trim();
    if (!v) {
      return 'Please enter your license number — a cosmetology, barber, nail or esthetics license is required to shop.';
    }
    if (/student/i.test(v)) {
      return 'Students: please use our student registration instead — the link is right above this form.';
    }
    var compact = v.replace(/[\s\-.]/g, '');
    if (compact.length < 4 || compact.length > 15) {
      return 'That license number doesn’t look right — most are 4–15 letters/numbers. Enter it exactly as it appears on your license.';
    }
    if (!/^[A-Za-z0-9]+$/.test(compact)) {
      return 'License numbers only contain letters and numbers.';
    }
    if (/^(.)\1+$/.test(compact)) {
      return 'That license number doesn’t look right. Enter it exactly as it appears on your license.';
    }
    var digits = compact.replace(/\D/g, '');
    if (digits.length >= 5 && (digits === compact || compact.length - digits.length <= 2)) {
      var asc = '01234567890123456789', desc = '98765432109876543210';
      if (asc.indexOf(digits) > -1 || desc.indexOf(digits) > -1) {
        return 'That license number doesn’t look right. Enter it exactly as it appears on your license.';
      }
    }
    return null;
  }

  function showFieldError(field, message) {
    var note = field.parentElement.querySelector('.obs-field-error');
    if (!note) {
      note = document.createElement('p');
      note.className = 'obs-field-error';
      field.parentElement.appendChild(note);
    }
    note.textContent = message;
    field.setAttribute('aria-invalid', 'true');
    field.focus();
  }

  function clearFieldError(field) {
    var note = field.parentElement.querySelector('.obs-field-error');
    if (note) note.remove();
    field.removeAttribute('aria-invalid');
  }

  function clearIfEmail(field) {
    if (!field) return;
    setTimeout(function () {
      if (looksLikeEmail(field.value)) field.value = '';
    }, 0);
  }

  function guardLicenseField(field) {
    if (!field || field.__obsGuarded) return;
    field.__obsGuarded = true;

    clearIfEmail(field);
    field.addEventListener('focus', function () { clearIfEmail(field); });
    // ogden-beauty.css attaches the `obs-autofill-detect` keyframe to
    // input[data-obs-license-number]:-webkit-autofill — fires the moment
    // Chrome paints an autofilled value into the field.
    field.addEventListener('animationstart', function (e) {
      if (e.animationName === 'obs-autofill-detect') clearIfEmail(field);
    });
  }

  function bindForm(anchor) {
    var form = anchor.closest('form');
    if (!form || form.__obsLicensedBound) return;
    form.__obsLicensedBound = true;

    var licNum = form.querySelector('[data-obs-license-number]');
    guardLicenseField(licNum);

    if (licNum) {
      licNum.addEventListener('input', function () { clearFieldError(licNum); });
    }
    var licState = form.querySelector('[data-obs-license-state]');
    if (licState) {
      licState.addEventListener('change', function () { clearFieldError(licState); });
    }
  }

  /* True when the sign-up must stop; the visitor has been told why. */
  function blockSubmit(form) {
    var cert = form.querySelector('[data-obs-certify]');
    if (cert && !cert.checked) {
      alert('Please certify that you are a licensed salon professional to continue.');
      cert.focus();
      return true;
    }
    var licNum = form.querySelector('[data-obs-license-number]');
    if (licNum && looksLikeEmail(licNum.value)) {
      showFieldError(licNum, 'That looks like an email address — enter your license number instead.');
      licNum.removeAttribute('readonly');
      licNum.value = '';
      return true;
    }
    if (licNum) {
      var problem = licenseProblem(licNum.value);
      if (problem) {
        showFieldError(licNum, problem);
        return true;
      }
    }
    var licState = form.querySelector('[data-obs-license-state]');
    if (licState && !licState.value) {
      showFieldError(licState, 'Please select the state that issued your license.');
      return true;
    }
    return false;
  }

  function init() {
    document.querySelectorAll('[data-obs-customer-note]').forEach(bindForm);
    if (window.__obsLicensedSubmitBound) return;
    window.__obsLicensedSubmitBound = true;
    window.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || !form.__obsLicensedBound) return;
      if (blockSubmit(form)) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    }, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
